// Sugalarm — voice conversation dispatcher (cycle 101). KIOSK-ONLY.
//
// Consumes recognized intents from `useVoiceAgent` and speaks answers via
// `useTTS`. The five read intents are answered from in-memory state (so
// they work OFFLINE); `logInsulin` runs a spoken confirm-back, then on
// "yes" inserts the dose optimistically (IOB ring starts immediately) and
// POSTs `/api/treatments` — queuing the write persistently if offline.
//
// State machine: idle → answering → idle, and for logInsulin
// idle → awaitingConfirm → (confirmYes → log) → idle. Hygiene: a wake
// barges in on an in-progress answer / cancels a pending confirm; voice is
// deferred entirely while an urgent alarm is sounding.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import * as treatmentsApi from "../api/treatments";
import * as carbsApi from "../api/carbs";
import type { CarbEstimate } from "../api/carbs";
import {
  composeGlucoseUtterance,
  resolveUtteranceContext,
} from "../lib/utterance";
import {
  enqueue,
  loadQueue,
  queueCount,
  removePending,
  treatmentMatchesPending,
} from "../lib/treatmentQueue";
import type { ApiErrorKind, Treatment, TreatmentCreateIn } from "../lib/types";
import {
  CANCELLED_TEXT,
  CANT_LOOKUP_CARBS_TEXT,
  MISS_TEXT,
  carbEstimateText,
  carbLoggedText,
  carbLowConfidenceText,
  carbNotesFromEstimate,
  confirmBackText,
  foodLabelFromItems,
  getIOBText,
  getInsulinTimeLeftText,
  getLastDoseText,
  loggedText,
} from "../lib/voiceIntents";
import type { GlucoseSocketState } from "./useGlucoseSocket";
import type { TTSState } from "./useTTS";
import type { VoiceAgentState, VoiceIntentEvent } from "./useVoiceAgent";

const CONFIRM_TIMEOUT_MS = 8000;

type ConvState = "idle" | "answering" | "awaitingConfirm";

// Cycle 102: the armed confirm carries the pending write — insulin units
// OR a carb estimate — so a single `confirmYes` routes to the right write.
type PendingConfirm =
  | { kind: "insulin"; units: number }
  | { kind: "carb"; estimate: CarbEstimate; description: string };

export interface UseVoiceDispatchOpts {
  /** True only on a kiosk with a reachable voice agent. */
  enabled: boolean;
  voice: VoiceAgentState;
  tts: TTSState;
  socket: GlucoseSocketState;
  activeMemberId: string | null;
  memberName: string | null;
  /** Suppress all voice answers while an urgent alarm loop is active —
   *  never speak over the alarm (mirrors the cycle-16 auto-announce
   *  urgent-suppression posture). */
  urgentActive: boolean;
}

export interface VoiceDispatchState {
  /** Count of treatment writes waiting to sync (offline queue). Surfaced
   *  as an unobtrusive "N pending sync" hint near the IOB card. */
  pendingCount: number;
}

function isTransient(kind: ApiErrorKind): boolean {
  return kind === "network" || kind === "server" || kind === "rate_limit" || kind === "unknown";
}

/** Valid voice dose: positive, ≤ 50, in half-unit steps. */
function isValidDose(units: number): boolean {
  return (
    Number.isFinite(units) &&
    units > 0 &&
    units <= 50 &&
    Number.isInteger(units * 2)
  );
}

function mergeTreatments(
  rest: Treatment[],
  live: Treatment[] | undefined,
  deleted: Set<string> | undefined,
): Treatment[] {
  const byId = new Map<string, Treatment>();
  for (const t of rest) byId.set(t.id, t);
  if (live) for (const t of live) byId.set(t.id, t);
  if (deleted) for (const id of deleted) byId.delete(id);
  return [...byId.values()];
}

export function useVoiceDispatch(opts: UseVoiceDispatchOpts): VoiceDispatchState {
  const { enabled, voice, tts, socket, activeMemberId, memberName, urgentActive } = opts;

  const [pendingCount, setPendingCount] = useState<number>(() => queueCount());

  // ─── treatments for answering (REST seed merged with live cache) ─────
  const [restTreatments, setRestTreatments] = useState<Treatment[]>([]);
  const refetch = useCallback(async () => {
    const r = await treatmentsApi.list(4, 50);
    if (r.ok) setRestTreatments(r.data.treatments);
  }, []);
  useEffect(() => {
    if (enabled) void refetch();
  }, [enabled, refetch, activeMemberId]);

  const liveCache = activeMemberId ? socket.byMember[activeMemberId]?.recentTreatments : undefined;
  const deletedIds = activeMemberId ? socket.byMember[activeMemberId]?.deletedTreatmentIds : undefined;
  const treatments = useMemo(
    () => mergeTreatments(restTreatments, liveCache, deletedIds),
    [restTreatments, liveCache, deletedIds],
  );

  // ─── refs so the intent handler reads current values, never stale ────
  const treatmentsRef = useRef(treatments);
  treatmentsRef.current = treatments;
  const latestReadingRef = useRef(socket.lastReading);
  latestReadingRef.current = socket.lastReading;
  // Cycle 309: the server-authored alert row mirror — the utterance
  // context's `alert_state` source (recovery truth for the gate).
  const lastAlertRef = useRef(socket.lastAlert);
  lastAlertRef.current = socket.lastAlert;
  const memberNameRef = useRef(memberName);
  memberNameRef.current = memberName;
  const activeMemberIdRef = useRef(activeMemberId);
  activeMemberIdRef.current = activeMemberId;
  const urgentRef = useRef(urgentActive);
  urgentRef.current = urgentActive;

  const convRef = useRef<ConvState>("idle");
  const pendingConfirmRef = useRef<PendingConfirm | null>(null);
  const confirmTimerRef = useRef<number | null>(null);

  const speakAnswer = useCallback(
    (text: string) => {
      convRef.current = "answering";
      tts.speak(text);
    },
    [tts],
  );

  const clearConfirm = useCallback(() => {
    if (confirmTimerRef.current != null) {
      window.clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
    pendingConfirmRef.current = null;
    convRef.current = "idle";
  }, []);

  // ─── the write path (optimistic insert + POST + offline queue) ───────
  // Generic over insulin / carbs: builds the optimistic row from the
  // payload, inserts it (Now-screen IOB ring or meal context updates
  // immediately), POSTs, reconciles, and queues on a transient failure.
  const performWrite = useCallback(
    async (
      payload: TreatmentCreateIn,
      successText: string,
      queuedText: string,
    ) => {
      const memberId = activeMemberIdRef.current;
      if (!memberId) {
        speakAnswer(MISS_TEXT);
        return;
      }
      const clientId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `voice-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
      const iso = payload.timestamp;
      const optimistic: Treatment = {
        id: clientId,
        member_id: memberId,
        user_id: "",
        event_type: payload.event_type,
        timestamp: iso,
        carbs_g: payload.carbs_g ?? null,
        insulin_u: payload.insulin_u ?? null,
        notes: payload.notes ?? null,
        entered_by: "voice",
        provenance: payload.provenance ?? { source: "voice" },
        created_at: iso,
      };
      socket.injectTreatment(memberId, optimistic);

      const res = await treatmentsApi.create(payload);
      if (res.ok) {
        // Reconcile: drop the optimistic row; the real server row arrives
        // via the cycle-24 `treatment` frame, and we inject it too so it's
        // present even if the socket is momentarily down.
        socket.forgetTreatment(memberId, clientId);
        socket.injectTreatment(memberId, res.data);
        speakAnswer(successText);
        void refetch();
      } else if (isTransient(res.error.kind)) {
        // Offline / transient — queue the write, KEEP the optimistic row.
        enqueue({ clientId, payload, createdAt: Date.now() });
        setPendingCount(queueCount());
        speakAnswer(queuedText);
      } else {
        // Permanent rejection — drop the optimistic row.
        socket.forgetTreatment(memberId, clientId);
        speakAnswer("Sorry, I couldn't log that.");
      }
    },
    [socket, speakAnswer, refetch],
  );

  const performLogInsulin = useCallback(
    (units: number) => {
      const iso = new Date().toISOString();
      const payload: TreatmentCreateIn = {
        event_type: "insulin",
        timestamp: iso,
        insulin_u: units,
        provenance: { source: "voice" },
      };
      void performWrite(payload, loggedText(units, false), loggedText(units, true));
    },
    [performWrite],
  );

  const performLogCarb = useCallback(
    (estimate: CarbEstimate, description: string) => {
      const iso = new Date().toISOString();
      const total = estimate.total_carbs_g;
      const payload: TreatmentCreateIn = {
        event_type: "carbs",
        timestamp: iso,
        carbs_g: total,
        notes: carbNotesFromEstimate(estimate.items, description),
        // Carry the estimator metadata so a future calibration cycle can
        // join estimate → actual (same shape the photo flow stores).
        provenance: {
          source: "voice",
          model: estimate.model,
          request_id: estimate.request_id,
          confidence: estimate.confidence,
        },
      };
      void performWrite(payload, carbLoggedText(total, false), carbLoggedText(total, true));
    },
    [performWrite],
  );

  // ─── offline queue flush ─────────────────────────────────────────────
  const flushInFlightRef = useRef(false);
  const flush = useCallback(async () => {
    if (!enabled || flushInFlightRef.current) return;
    const list = loadQueue();
    if (list.length === 0) {
      setPendingCount(0);
      return;
    }
    flushInFlightRef.current = true;
    const memberId = activeMemberIdRef.current;
    try {
      for (const p of list) {
        // Dedupe: did this write actually land before we went offline (a
        // lost POST response)? If a real server row matches, drop without
        // re-POSTing so we never duplicate.
        const landed = treatmentsRef.current.some((t) => treatmentMatchesPending(t, p));
        if (landed) {
          if (memberId) socket.forgetTreatment(memberId, p.clientId);
          removePending(p.clientId);
          continue;
        }
        const res = await treatmentsApi.create(p.payload);
        if (res.ok) {
          if (memberId) {
            socket.forgetTreatment(memberId, p.clientId);
            socket.injectTreatment(memberId, res.data);
          }
          removePending(p.clientId);
        } else if (isTransient(res.error.kind)) {
          break; // still offline — stop, retry on the next signal
        } else {
          if (memberId) socket.forgetTreatment(memberId, p.clientId);
          removePending(p.clientId); // permanent failure — drop
        }
      }
    } finally {
      flushInFlightRef.current = false;
      setPendingCount(queueCount());
      void refetch();
    }
  }, [enabled, socket, refetch]);

  // Flush on mount/enable, on socket (re)connect, and on online/visibility.
  useEffect(() => {
    if (enabled) void flush();
  }, [enabled, flush]);
  useEffect(() => {
    if (enabled && socket.status === "open") void flush();
  }, [enabled, socket.status, flush]);
  useEffect(() => {
    if (!enabled) return;
    const onOnline = () => void flush();
    const onVis = () => {
      if (document.visibilityState === "visible") void flush();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled, flush]);

  // ─── intent handling ─────────────────────────────────────────────────

  // Arm the yes/no confirm with a given pending write + spoken prompt.
  const armConfirm = useCallback(
    (pending: PendingConfirm, prompt: string) => {
      pendingConfirmRef.current = pending;
      convRef.current = "awaitingConfirm";
      tts.speak(prompt);
      if (confirmTimerRef.current != null) window.clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = window.setTimeout(() => {
        // Timed out with no yes/no — cancel silently.
        if (convRef.current === "awaitingConfirm") clearConfirm();
      }, CONFIRM_TIMEOUT_MS);
    },
    [tts, clearConfirm],
  );

  const handleLogInsulin = useCallback(
    (slots: Record<string, string>) => {
      const units = parseFloat(slots.units ?? "");
      if (!isValidDose(units)) {
        speakAnswer(MISS_TEXT);
        return;
      }
      armConfirm({ kind: "insulin", units }, confirmBackText(units));
    },
    [speakAnswer, armConfirm],
  );

  // Cycle 102 — carb lookup: text → AI estimate → speak → confirm-or-floor.
  const handleCarbQuery = useCallback(
    async (text: string) => {
      // Mark answering so a barge-in can interrupt; needs the network.
      convRef.current = "answering";
      const r = await carbsApi.estimateCarbs(text);
      if (!r.ok) {
        // 503 not-configured / network / upstream — can't look up carbs.
        speakAnswer(CANT_LOOKUP_CARBS_TEXT);
        return;
      }
      const est = r.data;
      const label = foodLabelFromItems(est.items);
      if (est.confidence === "low") {
        // Low-confidence floor (mirrors cycle 27): speak the number with
        // the caveat, do NOT offer to log a shaky estimate.
        speakAnswer(carbLowConfidenceText(est.total_carbs_g, label));
        return;
      }
      armConfirm(
        { kind: "carb", estimate: est, description: text },
        carbEstimateText(est.total_carbs_g, label),
      );
    },
    [speakAnswer, armConfirm],
  );

  const handleEvent = useCallback(
    (ev: VoiceIntentEvent) => {
      const now = Date.now();
      if (ev.type === "intent_miss") {
        // Mid-confirm noise cancels the pending confirm; either way, say so.
        if (convRef.current === "awaitingConfirm") clearConfirm();
        speakAnswer(MISS_TEXT);
        return;
      }
      if (ev.type === "carb_query") {
        // A carb question starts fresh — abandon any pending confirm.
        if (convRef.current === "awaitingConfirm") clearConfirm();
        void handleCarbQuery(ev.text);
        return;
      }
      const intent = ev.intent;
      const slots = ev.slots;
      if (convRef.current === "awaitingConfirm") {
        if (intent === "confirmYes") {
          const pending = pendingConfirmRef.current;
          clearConfirm();
          if (pending?.kind === "insulin") {
            performLogInsulin(pending.units);
          } else if (pending?.kind === "carb") {
            performLogCarb(pending.estimate, pending.description);
          }
        } else if (intent === "confirmNo") {
          clearConfirm();
          speakAnswer(CANCELLED_TEXT);
        }
        // Any other intent while awaiting is ignored (wait for yes/no or
        // the timeout).
        return;
      }
      // Not armed — confirm intents are ignored here by design.
      switch (intent) {
        case "getGlucose": {
          // Cycle 309: compose through the server-authoritative utterance
          // pipeline — context resolved NOW (refs are current at intent
          // time), zone from the reading's server-stamped classification
          // (never zoneOf + local thresholds), veracity gate applied. An
          // explicit query speaks the stale / no-data variants (the user
          // asked for the last value — the cycle-235.3 distinction).
          const ctx = resolveUtteranceContext({
            reading: latestReadingRef.current,
            memberId: activeMemberIdRef.current,
            memberName: memberNameRef.current,
            lastAlert: lastAlertRef.current,
            now,
          });
          const r = composeGlucoseUtterance(ctx, {
            surface: "voice_query",
            staleMode: "speak",
          });
          if (r.text) speakAnswer(r.text);
          break;
        }
        case "getIOB":
          speakAnswer(getIOBText(treatmentsRef.current, now));
          break;
        case "getInsulinTimeLeft":
          speakAnswer(getInsulinTimeLeftText(treatmentsRef.current, now));
          break;
        case "getLastDose":
          speakAnswer(getLastDoseText(treatmentsRef.current, now));
          break;
        case "logInsulin":
          handleLogInsulin(slots);
          break;
        // Unknown / confirm-when-not-armed → ignore (forward-compatible
        // with later cycles' intents).
        default:
          break;
      }
    },
    [clearConfirm, performLogInsulin, performLogCarb, speakAnswer, handleLogInsulin, handleCarbQuery],
  );

  // Stash in a ref so the intent effect depends only on the event ts.
  const handleEventRef = useRef(handleEvent);
  handleEventRef.current = handleEvent;
  const clearConfirmRef = useRef(clearConfirm);
  clearConfirmRef.current = clearConfirm;

  const lastIntent = voice.lastIntent;
  useEffect(() => {
    if (!enabled || !lastIntent) return;
    // Defer to alarms — never speak over an urgent loop.
    if (urgentRef.current) return;
    handleEventRef.current(lastIntent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastIntent?.ts, enabled]);

  // ─── barge-in on a wake ──────────────────────────────────────────────
  const lastWakeTs = voice.lastWakeTs;
  useEffect(() => {
    if (!enabled || lastWakeTs == null) return;
    if (urgentRef.current) return;
    if (convRef.current === "answering") {
      tts.stop();
      convRef.current = "idle";
    } else if (convRef.current === "awaitingConfirm") {
      clearConfirmRef.current(); // cancel the pending confirm, fresh start
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastWakeTs, enabled]);

  // Answer finished → back to idle (so a later wake's barge-in is a no-op).
  useEffect(() => {
    if (!tts.speaking && convRef.current === "answering") {
      convRef.current = "idle";
    }
  }, [tts.speaking]);

  return { pendingCount };
}
